import React, { useState, Fragment } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { Button } from 'components/ui';
import { Input, Textarea } from 'components/ui/Form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { generateOfferLetter } from 'services/offerLetterService';
import { toast } from 'sonner';
import { convertToUTC } from 'utils/dateUtils';

/**
 * GenerateOfferLetterModal Component
 * Form modal for generating a new offer letter
 */
const GenerateOfferLetterModal = ({
    isOpen,
    onClose,
    leadId,
    leadData,
    onSuccess = () => { },
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        candidateName: leadData?.name || '',
        position: '',
        salary: '',
        joiningDate: '',
        reportingTo: '',
        location: '',
    });

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.candidateName.trim()) {
            toast.error('Candidate name is required');
            return;
        }
        if (!formData.position.trim()) {
            toast.error('Position is required');
            return;
        }
        if (!formData.salary.trim()) {
            toast.error('Salary is required');
            return;
        }
        if (!formData.joiningDate) {
            toast.error('Joining date is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const requestData = {
                leadId: leadId,
                candidateName: formData.candidateName.trim(),
                position: formData.position.trim(),
                salary: formData.salary.trim(),
                joiningDate: convertToUTC(formData.joiningDate),
                reportingTo: formData.reportingTo.trim() || 'TBD',
                location: formData.location.trim() || 'TBD',
                additionalParameters: null,
            };

            const result = await generateOfferLetter(requestData);

            if (result.success || result.statusCode === 200) {
                toast.success(result.message || 'Offer letter generated successfully!');
                onSuccess();
                handleClose();
            } else {
                toast.error(result.message || 'Failed to generate offer letter');
            }
        } catch (error) {
            console.error('Error generating offer letter:', error);
            toast.error(error.response?.data?.message || 'Failed to generate offer letter');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        setFormData({
            candidateName: leadData?.name || '',
            position: '',
            salary: '',
            joiningDate: '',
            reportingTo: '',
            location: '',
        });
        onClose();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" onClose={handleClose} className="fixed inset-0 z-[130] overflow-y-auto">
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white dark:bg-dark-700 shadow-xl transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 px-6 py-4">
                                    <DialogTitle as="h3" className="text-lg font-semibold text-gray-900 dark:text-dark-50">
                                        Generate Offer Letter
                                    </DialogTitle>
                                    <Button
                                        onClick={handleClose}
                                        variant="flat"
                                        isIcon
                                        className="size-8 rounded-full"
                                        disabled={isSubmitting}
                                    >
                                        <XMarkIcon className="size-5" />
                                    </Button>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="px-6 py-4 space-y-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                            Fill in the details to generate an offer letter for {leadData?.name || 'this candidate'}.
                                        </p>

                                        {/* Candidate Name */}
                                        <Input
                                            label="Candidate Name"
                                            value={formData.candidateName}
                                            onChange={(e) => handleInputChange('candidateName', e.target.value)}
                                            required
                                            disabled={isSubmitting}
                                            placeholder="Enter candidate name"
                                        />

                                        {/* Position */}
                                        <Input
                                            label="Position / Role"
                                            value={formData.position}
                                            onChange={(e) => handleInputChange('position', e.target.value)}
                                            required
                                            disabled={isSubmitting}
                                            placeholder="e.g., Software Engineer, Marketing Manager"
                                        />

                                        {/* Salary */}
                                        <Input
                                            label="Salary / Compensation"
                                            value={formData.salary}
                                            onChange={(e) => handleInputChange('salary', e.target.value)}
                                            required
                                            disabled={isSubmitting}
                                            placeholder="e.g., $80,000 per annum, ₹12 LPA"
                                        />

                                        {/* Joining Date */}
                                        <Input
                                            label="Joining Date"
                                            type="date"
                                            value={formData.joiningDate}
                                            onChange={(e) => handleInputChange('joiningDate', e.target.value)}
                                            required
                                            disabled={isSubmitting}
                                        />

                                        {/* Reporting To */}
                                        <Input
                                            label="Reporting To (Optional)"
                                            value={formData.reportingTo}
                                            onChange={(e) => handleInputChange('reportingTo', e.target.value)}
                                            disabled={isSubmitting}
                                            placeholder="e.g., John Doe, Engineering Manager"
                                        />

                                        {/* Location */}
                                        <Input
                                            label="Work Location (Optional)"
                                            value={formData.location}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            disabled={isSubmitting}
                                            placeholder="e.g., New York, Remote, Hybrid"
                                        />
                                    </div>

                                    {/* Footer */}
                                    <div className="border-t border-gray-200 dark:border-dark-600 px-6 py-4 bg-gray-50 dark:bg-dark-800 flex justify-end gap-3">
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            onClick={handleClose}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Generating...' : 'Generate Offer Letter'}
                                        </Button>
                                    </div>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default GenerateOfferLetterModal;
